import React from "react";
import { useCandidateLayout } from "../../Components/Context/CandidateLayoutContext";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CircleIcon from "@mui/icons-material/Circle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { useNavigate } from "react-router-dom";
import CommonButton from "../../Components/InputComponents/CommonButton";

function CandidateJobs() {
  const { candidateData, candidateJobData } = useCandidateLayout();

  const Navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 1220px)");

  return (
    <Box
      sx={{
        p: 4,
        mx: "auto",
        margin: "auto",
        padding: "20px",
        maxWidth: isMobile ? "100vw" : `calc(100vw - 300px)`,
      }}
    >
      <CommonButton
        background="#006CB8"
        textColor="white"
        onClick={() => Navigate(`/`)}
        sx={{
          minWidth: "100px",
          color: "white",
          mt: 0.5,
          borderRadius: "20px",
          height: "30px",
          textTransform: "capitalize",
        }}
      >
        Back
      </CommonButton>
      <Typography
        sx={{
          fontFamily: "Geist, sans-serif",
          fontSize: { xs: "16px", sm: "18px", lg: "22px" },
          fontWeight: 700,
          margin: "10px auto",
        }}
      >
        Jobs
      </Typography>
      {/* Job Procedure Mapping */}
      <Grid container>
        {candidateJobData?.map((job, index) => {
          const jobTitle = job?.JD_Title;
          const jobId = job?.JD_JobId;
          const stages = job?.recruitment_json
            ? JSON.parse(job.recruitment_json)?.stages || []
            : [];

          return (
            <Grid
              item
              xs={12}
              key={index}
              sx={{
                border: "1px solid lightgrey",
                margin: "10px auto",
                borderRadius: "10px",
                padding: "15px",
              }}
            >
              {/* Application Progress - Job Title */}
              <Typography
                sx={{
                  px: 2,
                  textAlign: "left",
                  fontFamily: "Geist, sans-serif",
                  letterSpacing: `0.5px`,
                  fontSize: "22px",
                  color: "#333",
                  fontWeight: "600",
                }}
              >
                ({jobId}) - {jobTitle}
              </Typography>

              {/* Horizontal Scrollable Box of Stages */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  padding: 2,
                }}
              >
                {stages.map((stage, stageIndex) => {
                  const hasSubProcedures =
                    Array.isArray(stage.subProcedures) &&
                    stage.subProcedures.length > 0;

                  // Check completion per subProcedure
                  const subComponents = hasSubProcedures
                    ? stage.subProcedures.map((sub) => {
                        const fields = sub?.fields || [];
                        const isSubCompleted = fields.some(
                          (field) =>
                            field?.RPF_Type === "IsCompleted" &&
                            Array.isArray(field?.RPF_Value) &&
                            field?.RPF_Value.includes("1")
                        );

                        const subStatusField = fields.find((field) =>
                          field?.RPF_Name?.trim()
                            ?.toLowerCase()
                            .includes("status")
                        );

                        return {
                          ...sub,
                          isCompleted: isSubCompleted,
                          statusValue: Array.isArray(subStatusField?.RPF_Value)
                            ? subStatusField.RPF_Value.join(", ")
                            : subStatusField?.RPF_Value || "",
                        };
                      })
                    : [];

                  // Stage is complete if all subs are complete or own fields say so
                  const stageIsCompleted = hasSubProcedures
                    ? subComponents.length > 0 &&
                      subComponents.every((s) => s.isCompleted)
                    : stage?.fields?.some(
                        (field) =>
                          field?.RPF_Type === "IsCompleted" &&
                          Array.isArray(field?.RPF_Value) &&
                          field?.RPF_Value.includes("1")
                      );

                  const stageBorderColor = stageIsCompleted
                    ? "#22C55E"
                    : "#3B82F6";
                  const stageBgColor = stageIsCompleted
                    ? "#22C55E1A"
                    : "#3B82F61A";

                  // Search all stage + sub fields for first "status" match
                  const allFields = [
                    ...(stage?.fields || []),
                    ...(stage?.subProcedures || []).flatMap(
                      (sub) => sub?.fields || []
                    ),
                  ];

                  const stageStatusField = allFields.find((field) =>
                    field?.RPF_Name?.trim()?.toLowerCase().includes("status")
                  );

                  return (
                    <Box
                      key={stageIndex}
                      sx={{
                        minWidth: "200px",
                        minHeight: "150px",
                        border: `2px solid ${stageBorderColor}`,
                        backgroundColor: stageBgColor,
                        borderRadius: 2,
                        p: 2,
                        m: 2,
                        boxShadow: "0px 2px 6px rgba(0,0,0,0.08)",
                        display: hasSubProcedures ? "block" : "flex",
                        flexDirection: hasSubProcedures ? "initial" : "column",
                        alignItems: hasSubProcedures ? "initial" : "center",
                        justifyContent: hasSubProcedures ? "initial" : "center",
                        textAlign: hasSubProcedures ? "initial" : "center",
                      }}
                    >
                      {/* Main Stage */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          alignContent: "center",
                          justifyContent: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        {stageIsCompleted ? (
                          <CheckCircleIcon
                            sx={{ color: "#22C55E", fontSize: 35 }}
                          />
                        ) : (
                          <AccessTimeIcon
                            sx={{ color: "#3B82F6", fontSize: 35 }}
                          />
                        )}
                        <Typography
                          fontWeight={600}
                          fontSize={16}
                          sx={{
                            textTransform: "capitalize",
                            fontFamily: "Geist, sans-serif",
                          }}
                        >
                          {stage?.RPS_Name || "Unnamed Stage"}
                        </Typography>
                        {stageStatusField && (
                          <Typography
                            sx={{
                              color: stageBorderColor,
                              fontFamily: "Geist, sans-serif",
                            }}
                            fontSize={14}
                          >
                            {Array.isArray(stageStatusField.RPF_Value)
                              ? stageStatusField.RPF_Value.join(", ")
                              : stageStatusField.RPF_Value || ""}
                          </Typography>
                        )}
                      </Box>

                      {/* SubTabs (if any) */}
                      {hasSubProcedures &&
                        subComponents.map((sub, subIndex) => {
                          const subColor = sub.isCompleted
                            ? "#22C55E"
                            : "#3B82F6";
                          const subBgColor = sub.isCompleted
                            ? "#22C55E1A"
                            : "#3B82F61A";

                          return (
                            <Box
                              key={subIndex}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                borderLeft: `4px solid ${subColor}`,
                                backgroundColor: subBgColor,
                                borderRadius: 1,
                                px: 1.5,
                                py: 1,
                                mb: 1,
                              }}
                            >
                              <Typography
                                fontSize={14}
                                fontWeight={500}
                                sx={{
                                  textTransform: "capitalize",
                                  fontFamily: "Geist, sans-serif",
                                }}
                              >
                                {sub?.RSP_Name || "Unnamed Sub"}
                              </Typography>
                              {sub.statusValue && (
                                <Typography
                                  fontSize={13}
                                  sx={{
                                    color: subColor,
                                    textTransform: "capitalize",
                                    fontFamily: "Geist, sans-serif",
                                  }}
                                >
                                  {sub.statusValue}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default CandidateJobs;
